import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ScanBarcode, Check, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import BarcodeScanner from "@/pages/BarcodeScanner";
import { useUsbScanner } from "@/lib/useUsbScanner";

const errMsg = (e) => e?.response?.data?.detail || (e?.response?.status === 400 ? "That barcode doesn't look valid" : "Couldn't create a draft");

async function draftFromBarcode(code) {
  const { data: run } = await api.post("/research", { barcode: code });
  const { data } = await api.post(`/research/${run.id}/to-draft`, {});
  return { id: data.id, title: run.title, price: run.suggested_price };
}

function SessionList({ session, onDone }) {
  const ready = session.filter((s) => s.id).length;
  return (
    <div className="flex min-h-0 flex-col gap-2" data-testid="rapid-session">
      <ul className="max-h-48 overflow-y-auto overscroll-contain">
        {session.map((s) => (
          <li key={s.code} className="flex items-center gap-2 border-t border-slate-100 py-1.5 text-xs" data-testid={`rapid-item-${s.code}`}>
            {s.status === "working" ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-slate-400" />
              : s.status === "error" ? <AlertCircle className="h-3.5 w-3.5 shrink-0 text-rose-500" />
              : <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" />}
            <span className="min-w-0 flex-1 truncate text-slate-800">{s.status === "error" ? `${s.code} — ${s.error}` : s.title || s.code}</span>
            {s.qty > 1 && <span className="shrink-0 rounded-full bg-slate-100 px-1.5 font-mono font-semibold text-slate-700" data-testid={`rapid-qty-${s.code}`}>×{s.qty}</span>}
            {s.price ? <span className="shrink-0 font-mono text-slate-500">${s.price}</span> : null}
          </li>
        ))}
      </ul>
      <Button onClick={onDone} disabled={!ready} className="w-full rounded-full" data-testid="rapid-done-btn">
        Done · {ready} draft{ready === 1 ? "" : "s"}
      </Button>
    </div>
  );
}

export default function ScanToDraftButton({ onCreated, disabled }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rapid, setRapid] = useState(false);
  const [session, setSession] = useState([]);
  const sessionRef = useRef([]);
  const queue = useRef(Promise.resolve());

  const setList = (list) => { sessionRef.current = list; setSession(list); };
  const update = (code, patch) => setList(sessionRef.current.map((s) => (s.code === code ? { ...s, ...patch } : s)));

  const rapidScan = (code) => {
    const existing = sessionRef.current.find((s) => s.code === code);
    if (!existing) setList([{ code, status: "working", qty: 1 }, ...sessionRef.current]);
    else if (existing.status === "error") update(code, { status: "working", qty: 1 });
    else {
      update(code, { qty: existing.qty + 1 });
      toast(`+1 ${existing.title ? existing.title.slice(0, 30) : code}`);
    }
    queue.current = queue.current.then(async () => {
      const item = sessionRef.current.find((s) => s.code === code);
      try {
        if (!item.id) update(code, { ...(await draftFromBarcode(code)), status: "done" });
        const fresh = sessionRef.current.find((s) => s.code === code);
        if (fresh.qty > 1) await api.patch(`/drafts/${fresh.id}`, { quantity: String(fresh.qty) });
      } catch (e) {
        if (!item.id) update(code, { status: "error", error: errMsg(e) });
        else toast.error("Couldn't update quantity");
      }
    });
  };

  const singleScan = async (code) => {
    setOpen(false);
    setBusy(true);
    try {
      const d = await draftFromBarcode(code);
      toast.success(`Draft ready: ${d.title.slice(0, 40)} — add photos next`);
      onCreated(d.id);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  useUsbScanner(singleScan, !open && !busy && !disabled);

  const finish = async () => {
    await queue.current;
    const ids = sessionRef.current.filter((s) => s.id).map((s) => s.id);
    setOpen(false);
    if (ids.length) navigate(`/library?ids=${ids.join(",")}`);
  };

  const onOpenChange = (o) => {
    if (o) setList([]);
    else if (sessionRef.current.some((s) => s.id)) toast.success("Rapid scan drafts are saved in your Library");
    setOpen(o);
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => onOpenChange(true)} disabled={busy || disabled}
        className="h-9 rounded-full" data-testid="scan-to-draft-button">
        {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <ScanBarcode className="mr-1 h-4 w-4" />}
        {busy ? "Looking up…" : "Scan barcode"}
      </Button>
      <BarcodeScanner open={open} onOpenChange={onOpenChange} onDetected={rapid ? rapidScan : singleScan} continuous={rapid}>
        <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2 text-sm">
          <span>
            <span className="font-medium text-slate-900">Rapid mode</span>
            <span className="block text-[11px] text-slate-500">Keep scanning — a draft is made for each barcode. Repeats add +1 quantity.</span>
          </span>
          <Switch checked={rapid} onCheckedChange={setRapid} data-testid="rapid-mode-switch" />
        </label>
        {rapid && <SessionList session={session} onDone={finish} />}
      </BarcodeScanner>
    </>
  );
}
